import {
  formatLocalDate,
  daysSinceLocalDate,
} from "@freightsimple/time/helpers";
import type { LocalDate } from "@freightsimple/time/branded-types";
import {
  formatRelativeDaysShort,
  getRelativeColorFromDays,
  badgeColorStyles,
} from "../Internal/relativeTime";
import type { RelativeMode } from "../Internal/relativeTime";

const dateFormats: Record<string, string> = {
  iso: "",
  short: "MMM D",
  medium: "MMM D, YYYY",
  long: "ddd, MMM D, YYYY",
  monthYear: "MMMM YYYY",
  dayMonth: "ddd MMMM Do",
};

type DateFormat =
  | "iso"
  | "short"
  | "medium"
  | "long"
  | "monthYear"
  | "dayMonth"
  | (string & {});

interface DateProps {
  /** ISO date string to display */
  value: LocalDate | undefined;
  /** Format preset or custom dayjs format string (default: "medium") */
  format?: DateFormat | undefined;
  /**
   * Show absolute + relative badge.
   * - `true` — muted badge (for events: "3d ago")
   * - `"age"` — green→gold→red as time passes
   * - `"deadline"` — green when far, gold when close, red when overdue
   */
  relative?: RelativeMode | undefined;
  /**
   * Show relative badge only, no absolute date.
   * - `true` — muted badge
   * - `"age"` — green→gold→red as time passes
   * - `"deadline"` — green→gold→red as deadline approaches
   */
  relativeOnly?: RelativeMode | undefined;
}

function resolveStrategy(mode: RelativeMode) {
  return mode === true ? undefined : mode;
}

function RelativeBadge_({
  value,
  mode,
}: {
  value: LocalDate;
  mode: RelativeMode;
}) {
  const days = daysSinceLocalDate(value);
  const label = formatRelativeDaysShort(days);
  const color = getRelativeColorFromDays(days, resolveStrategy(mode));
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-px text-[10px] font-bold ${badgeColorStyles[color]}`}
    >
      {label}
    </span>
  );
}

export function Date({
  value,
  format = "medium",
  relative,
  relativeOnly,
}: DateProps) {
  if (!value) return <span>{"\u2014"}</span>;

  if (relativeOnly) {
    return <RelativeBadge_ value={value} mode={relativeOnly} />;
  }

  const formatted =
    format === "iso"
      ? value
      : formatLocalDate(value, dateFormats[format] ?? format, "\u2014");

  if (relative) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span>{formatted}</span>
        <RelativeBadge_ value={value} mode={relative} />
      </span>
    );
  }

  return <span>{formatted}</span>;
}
